import { useState } from "react"
import { useSelectedAccount } from "../../contexts/AccountContext"
import Modal from "../Modal"
import PlanBadge from "./PlanBadge"
import { isAtLeastPlan } from "../../lib/plan"
import type { InstagramAccount } from "../settings/AccountsTab"

type Props = {
  onClose: () => void
}

// account が横断表示（複数アカウント同時比較）に使えるプランかどうか
const isCrossAccountEligible = (account: InstagramAccount) =>
  isAtLeastPlan(account.plan, account.features.cross_account_min_plan)

const AccountSelectorModal = ({ onClose }: Props) => {
  const { organizations, selectedAccountIds, setSelectedAccountIds, singleAccountOnly } = useSelectedAccount()
  const [pending, setPending] = useState<number[]>(selectedAccountIds)

  const allAccounts = organizations.flatMap(o => o.accounts)
  const accountOf = (id: number) => allAccounts.find(a => a.id === id)
  const showUpsell = allAccounts.some(a => !isCrossAccountEligible(a))

  // 横断表示可能なアカウントの複数選択。単独選択から切り替えた場合は単独選択を破棄する
  const toggleBusiness = (id: number) => {
    if (singleAccountOnly) {
      setPending([id])
      return
    }
    setPending(prev => {
      const prevAllEligible = prev.length > 0 && prev.every(pid => {
        const acc = accountOf(pid)
        return !!acc && isCrossAccountEligible(acc)
      })
      if (!prevAllEligible) return [id]
      return prev.includes(id) ? prev.filter(i => i !== id) : [ ...prev, id ]
    })
  }

  // 単独表示のみのアカウント選択。他の選択をすべてクリアして単独化する
  const selectSingle = (id: number) => setPending([id])

  const handleApply = () => {
    setSelectedAccountIds(pending)
    onClose()
  }

  const renderRow = (account: InstagramAccount, opts: { multi: boolean; selected: boolean; onToggle: () => void }) => (
    <div
      key={account.id}
      className={`account-selector-modal__item${opts.selected ? " account-selector-modal__item--selected" : ""}`}
      onClick={opts.onToggle}
      role={opts.multi ? "checkbox" : "radio"}
      aria-checked={opts.selected}
      tabIndex={0}
      onKeyDown={e => e.key === "Enter" && opts.onToggle()}
    >
      <input
        type={opts.multi ? "checkbox" : "radio"}
        checked={opts.selected}
        onChange={opts.onToggle}
        onClick={e => e.stopPropagation()}
      />
      {account.avatar_url
        ? <img src={account.avatar_url} alt={account.username} className="account-selector-modal__avatar" />
        : <i className="bi bi-instagram" />
      }
      <span className="account-selector-modal__username">@{account.username}</span>
      {!opts.multi && <PlanBadge plan={account.plan} />}
      {!account.connected && (
        <span className="account-selector-modal__badge account-selector-modal__badge--disconnected">未接続</span>
      )}
    </div>
  )

  return (
    <Modal title="アカウントを選択" onClose={onClose}>
      {singleAccountOnly && (
        <p style={{ fontSize: 13, color: "#92400e", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 8, padding: "8px 12px", marginBottom: 12 }}>
          複数期間を指定中は1アカウントのみ選択できます
        </p>
      )}
      {organizations.length === 0 && (
        <p style={{ color: "#9ca3af", fontSize: 14 }}>アカウントが見つかりません</p>
      )}
      {organizations.map(org => {
        const businessAccounts = org.accounts.filter(isCrossAccountEligible)
        const soloAccounts     = org.accounts.filter(a => !isCrossAccountEligible(a))

        return (
          <div key={org.id} className="account-selector-modal__org">
            <div className="account-selector-modal__org-name">{org.name}</div>

            {org.accounts.length === 0 && (
              <p className="account-selector-modal__empty">連携済みアカウントがありません</p>
            )}

            {org.accounts.length > 0 && (
              <>
                <div className="account-selector-modal__section-label">同時表示できる（ビジネス）</div>
                {businessAccounts.length === 0 ? (
                  <p className="account-selector-modal__empty">ビジネスプランにすると複数アカウントを同時比較できます</p>
                ) : (
                  businessAccounts.map(account => renderRow(account, {
                    multi:    true,
                    selected: pending.includes(account.id),
                    onToggle: () => toggleBusiness(account.id),
                  }))
                )}

                {soloAccounts.length > 0 && (
                  <>
                    <div className="account-selector-modal__section-label">単独表示のみ 🔒</div>
                    {soloAccounts.map(account => renderRow(account, {
                      multi:    false,
                      selected: pending.length === 1 && pending[0] === account.id,
                      onToggle: () => selectSingle(account.id),
                    }))}
                  </>
                )}
              </>
            )}
          </div>
        )
      })}
      <div className="account-selector-modal__footer">
        {showUpsell && (
          <p className="account-selector-modal__upsell">ビジネスプランで同時比較できます</p>
        )}
        <button className="account-selector-modal__apply-btn" onClick={handleApply}>
          {pending.length}件を選択して適用
        </button>
      </div>
    </Modal>
  )
}

export default AccountSelectorModal
